import { useState } from 'react'
import { MediaImg } from '~/components/ui/MediaImg'
import type { MediaImage } from '~/lib/media/media-image'

export type Slide = { image: MediaImage; alt: string }

/** Minimal image slider (replacement for the Webflow slider): arrows + dots. */
export function ImageSlider({
  slides,
  label = 'Photos',
  className = '',
  sizes,
}: {
  slides: Slide[]
  /** Accessible name for the carousel. */
  label?: string
  className?: string
  /** Rendered slide width hint, e.g. "(min-width: 992px) 940px, 90vw". */
  sizes: string
}) {
  const [i, setI] = useState(0)
  const go = (n: number) => setI((n + slides.length) % slides.length)
  const arrow =
    'absolute top-1/2 z-10 -translate-y-1/2 cursor-pointer bg-black/40 px-3 py-2 text-2xl text-white transition-colors hover:bg-black/60'
  return (
    <div
      role="group"
      aria-roledescription="carousel"
      aria-label={label}
      className={`relative overflow-hidden bg-black ${className}`}
    >
      {/* Polite live region: Prev/Next/dots change the slide, which is announced. */}
      <div aria-live="polite" className="absolute inset-0">
        {slides.map((s, n) => (
          // Faded-out slides stay in the DOM; `inert` keeps them out of the
          // accessibility tree.
          <div
            key={`${n}-${s.image.src}`}
            role="group"
            aria-roledescription="slide"
            aria-label={`${n + 1} of ${slides.length}`}
            inert={n !== i}
            className={`absolute inset-0 transition-opacity duration-500 ${n === i ? 'opacity-100' : 'opacity-0'}`}
          >
            <MediaImg
              image={s.image}
              sizes={sizes}
              priority={n === 0}
              alt={s.alt}
              className="size-full object-cover"
            />
          </div>
        ))}
      </div>
      <button
        type="button"
        aria-label="Previous slide"
        className={`${arrow} left-0`}
        onClick={() => go(i - 1)}
      >
        ‹
      </button>
      <button
        type="button"
        aria-label="Next slide"
        className={`${arrow} right-0`}
        onClick={() => go(i + 1)}
      >
        ›
      </button>
      <div className="absolute inset-x-0 bottom-3 z-10 flex justify-center gap-2">
        {slides.map((s, n) => (
          <button
            key={`${n}-${s.image.src}`}
            type="button"
            aria-label={`Go to slide ${n + 1}`}
            aria-current={n === i ? 'true' : undefined}
            onClick={() => setI(n)}
            className={`h-2.5 w-2.5 cursor-pointer rounded-full transition-colors ${n === i ? 'bg-white' : 'bg-white/50 hover:bg-white/80'}`}
          />
        ))}
      </div>
    </div>
  )
}
